import {
    createAsyncThunk,
    createSlice
} from '@reduxjs/toolkit';
import apiService from '../../Services/apiServices';
const initialState = {
    partnerId: '',
    partnerName: '',
    outlets: [],
    members: [],
    plans: [],
    outletPlansByOutlet: {},
    outletSubscriptions: {},
    planCharges: {
        starter: 599,
        growth: 1499,
        pro: 1999
    },
    emailVerificationRequired: true,
    billingSummary: {
        partner_upi_id: null,
        summary: {
            pending_count: 0,
            pending_amount: 0,
            collections_count: 0,
            collections_amount: 0,
            next_payments_count: 0,
            overdue_count: 0,
            on_hold_count: 0
        },
        pending_payments: [],
        payment_history: [],
        next_payments: [],
        overdue_payments: [],
        on_hold_payments: []
    },
    alertQueue: [],
    selectedOutletId: '',
    selectedMemberOutletFilter: 'ALL',
    status: 'idle',
    actionStatus: 'idle',
    error: null
};
const getSimulatedPartnerId = () => {
    try {
        const raw = localStorage.getItem('ifit_sim_partner_id');
        return raw || '';
    } catch (e) {
        return '';
    }
};
const setSimulatedPartnerId = (partnerId) => {
    try {
        localStorage.setItem('ifit_sim_partner_id', partnerId);
    } catch (e) {
        // no-op
    }
};

const formatApiDetail = (detail, fallback) => {
    if (!detail) {
        return fallback;
    }
    if (typeof detail === 'string') {
        return detail;
    }
    if (Array.isArray(detail)) {
        const messages = detail.map((item) => {
            if (typeof item === 'string') {
                return item;
            }
            if (!item || typeof item !== 'object') {
                return '';
            }
            const field = Array.isArray(item.loc)
                ? item.loc.filter((part) => part !== 'body').join('.')
                : '';
            if (field === 'duration_months') {
                return 'Set the duration for at least one month.';
            }
            return [field, item.msg].filter(Boolean).join(': ');
        }).filter(Boolean);
        return messages.length > 0 ? messages.join(' ') : fallback;
    }
    if (typeof detail === 'object') {
        return detail.message || detail.msg || JSON.stringify(detail);
    }
    return String(detail);
};

const extract = (error, fallback) => formatApiDetail(error?.response?.data?.detail, fallback);

const asText = (value) => {
    if (typeof value !== 'string') {
        return '';
    }
    const trimmed = value.trim();
    return trimmed;
};

const pickFirstText = (sources, keys) => {
    for (const source of sources) {
        if (!source || typeof source !== 'object') {
            continue;
        }
        for (const key of keys) {
            const candidate = asText(source[key]);
            if (candidate) {
                return candidate;
            }
        }
    }
    return '';
};

const parseJwtPayload = (token) => {
    try {
        if (!token || token.split('.').length < 2) {
            return null;
        }
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
        return JSON.parse(atob(padded));
    } catch (e) {
        return null;
    }
};

const getAuthSnapshot = () => {
    try {
        const authRaw = localStorage.getItem('ifit_auth');
        const authRecord = authRaw ? JSON.parse(authRaw) : null;
        const tokenPayload = parseJwtPayload(authRecord?.access_token);
        return {
            authRecord,
            tokenPayload
        };
    } catch (e) {
        return {
            authRecord: null,
            tokenPayload: null
        };
    }
};

const getPartnerIdFromAuthToken = () => {
    const {
        authRecord,
        tokenPayload
    } = getAuthSnapshot();
    const tokenUserId = tokenPayload?.user_id || tokenPayload?.id || tokenPayload?.sub;
    if (tokenUserId) {
        return String(tokenUserId);
    }
    if (authRecord?.user_id) {
        return String(authRecord.user_id);
    }
    return '';
};

const resolvePartnerName = ({
    authRecord,
    tokenPayload,
    outlets,
    members
}) => {
    const topLevelName = pickFirstText([
        authRecord,
        tokenPayload
    ], [
        'partner_name',
        'full_name',
        'name',
        'user_name',
        'username'
    ]);
    if (topLevelName) {
        return topLevelName;
    }
    const outletName = pickFirstText(outlets, [
        'partner_name',
        'associated_partner_name',
        'owner_name',
        'created_by_name'
    ]);
    if (outletName) {
        return outletName;
    }
    const memberName = pickFirstText(members, ['partner_name', 'associated_partner_name']);
    if (memberName) {
        return memberName;
    }
    return '';
};

export const bootstrapPartnerWorkspace = createAsyncThunk('partnerWorkspace/bootstrap', async (_,
    thunkAPI) => {
                    try {
                        const authSnapshot = getAuthSnapshot();
                        const partnerId = getPartnerIdFromAuthToken() || getSimulatedPartnerId();
                        if (!partnerId) {
                            return thunkAPI.rejectWithValue(
                                'Unable to resolve partner account from login session.');
                        }
                        setSimulatedPartnerId(partnerId);
                        const profilePromise = apiService.get('/auth/me').catch(() => null);
                        const [profileResponse, outletsResponse, membersResponse, settingsResponse] =
                        await Promise.all([profilePromise, apiService.get('/barbellOSAdmin/outlets/get', {
                                partner_id: partnerId
                            }), apiService.get(`/barbellOSMember/members/${partnerId}/myMembers`),
                            apiService.get('/barbellOSAdmin/settings')
                        ]);
                        const profile = profileResponse?.data?.data || profileResponse?.data || {};
                        const outlets = Array.isArray(outletsResponse?.data?.data) ? outletsResponse.data.data :
                            [];
                        const members = Array.isArray(membersResponse?.data?.data) ? membersResponse.data.data :
                            [];
                        const partnerName = resolvePartnerName({
                            authRecord: {
                                ...(authSnapshot.authRecord || {}),
                                ...profile
                            },
                            tokenPayload: {
                                ...(authSnapshot.tokenPayload || {}),
                                ...profile
                            },
                            outlets,
                            members
                        });
                        const defaultOutletId = outlets[0]?.id ? String(outlets[0].id) : '';
                        let plans = [];
                        if (defaultOutletId) {
                            const plansResponse = await apiService.get(
                                `/barbellOSOutlet/plans/${defaultOutletId}/all`);
                            plans = Array.isArray(plansResponse?.data?.data) ? plansResponse.data.data : [];
                        }
                        return {
                            partnerId,
                            partnerName,
                            outlets,
                            members,
                            plans,
                            selectedOutletId: defaultOutletId,
                            emailVerificationRequired: settingsResponse?.data?.data
                                ?.email_verification_required !== false,
                            planCharges: settingsResponse?.data?.data?.plan_charges || {
                                starter: 599,
                                growth: 1499,
                                pro: 1999
                            }
                        };
                    } catch (error) {
                        return thunkAPI.rejectWithValue(extract(error, 'Failed to load partner workspace'));
                    }
                });
            export const fetchPlansByOutlet = createAsyncThunk('partnerWorkspace/fetchPlansByOutlet', async (outletId,
                thunkAPI) => {
                try {
                    const response = await apiService.get(`/barbellOSOutlet/plans/${outletId}/all`);
                    return {
                        outletId: String(outletId),
                        plans: Array.isArray(response?.data?.data) ? response.data.data : []
                    };
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to load plans'));
                }
            });
            export const fetchPlansForOutlets = createAsyncThunk('partnerWorkspace/fetchPlansForOutlets', async (
                outlets, thunkAPI) => {
                try {
                    const entries = await Promise.all((outlets || []).map(async (outlet) => {
                        const outletId = String(outlet.id || outlet);
                        const response = await apiService.get(
                            `/barbellOSOutlet/plans/${outletId}/all`);
                        return [outletId, Array.isArray(response?.data?.data) ? response
                            .data.data : []
                        ];
                    }));
                    return Object.fromEntries(entries);
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to load outlet plans'));
                }
            });
            export const addPlan = createAsyncThunk('partnerWorkspace/addPlan', async (payload, thunkAPI) => {
                try {
                    await apiService.post('/barbellOSOutlet/plans/add', payload);
                    await thunkAPI.dispatch(fetchPlansByOutlet(payload.outlet_id));
                    return true;
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to add plan'));
                }
            });
            export const editPlan = createAsyncThunk('partnerWorkspace/editPlan', async ({
                outletPlanId,
                payload,
                outletId
            }, thunkAPI) => {
                try {
                    await apiService.put(`/barbellOSOutlet/plans/${outletPlanId}/edit`, payload);
                    await thunkAPI.dispatch(fetchPlansByOutlet(outletId));
                    return true;
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to edit plan'));
                }
            });
            export const togglePlan = createAsyncThunk('partnerWorkspace/togglePlan', async ({
                outletPlanId,
                activate,
                outletId
            }, thunkAPI) => {
                try {
                    await apiService.put(
                        `/barbellOSOutlet/plans/${outletPlanId}/${activate ? 'activate' : 'deactivate'}`
                        );
                    await thunkAPI.dispatch(fetchPlansByOutlet(outletId));
                    return true;
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to update plan status'));
                }
            });
            export const deletePlan = createAsyncThunk('partnerWorkspace/deletePlan', async ({
                outletPlanId,
                outletId
            }, thunkAPI) => {
                try {
                    await apiService.delete(`/barbellOSOutlet/plans/${outletPlanId}/delete`);
                    await thunkAPI.dispatch(fetchPlansByOutlet(outletId));
                    return true;
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to delete plan'));
                }
            });
            export const refreshMembers = createAsyncThunk('partnerWorkspace/refreshMembers', async (partnerId,
                thunkAPI) => {
                try {
                    const response = await apiService.get(
                    `/barbellOSMember/members/${partnerId}/myMembers`);
                    return Array.isArray(response?.data?.data) ? response.data.data : [];
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to load members'));
                }
            });
            export const addMemberStepOne = createAsyncThunk('partnerWorkspace/addMemberStepOne', async (payload,
                thunkAPI) => {
                try {
                    const response = await apiService.post('/barbellOSMember/members/onboard/a-step',
                        payload);
                    return response?.data?.data || null;
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to add member'));
                }
            });
            export const verifyMemberOtp = createAsyncThunk('partnerWorkspace/verifyMemberOtp', async (payload,
                thunkAPI) => {
                try {
                    const response = await apiService.post(
                        '/barbellOSMember/members/onboard/b-step/verifyOTP', payload);
                    return response?.data?.data || null;
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to verify member OTP'));
                }
            });
            export const uploadMemberDocuments = createAsyncThunk('partnerWorkspace/uploadMemberDocuments', async ({
                files,
                userId,
                outletId,
                docType,
                partnerId
            }, thunkAPI) => {
                try {
                    const formData = new FormData();
                    files.forEach((file) => formData.append('documents', file));
                    formData.append('user_id', userId);
                    formData.append('outlet_id', outletId);
                    formData.append('doc_type', docType);
                    await apiService.post('/barbellOSMember/members/upload/member-documents', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                    await thunkAPI.dispatch(refreshMembers(partnerId));
                    return true;
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to upload member documents'));
                }
            });
            export const fetchAvailablePlansForMember = createAsyncThunk(
                'partnerWorkspace/fetchAvailablePlansForMember', async (outletId, thunkAPI) => {
                    try {
                        const response = await apiService.get(
                            `/barbellOSOutlet/plans/${outletId}/available-plans`);
                        return Array.isArray(response?.data?.data) ? response.data.data : [];
                    } catch (error) {
                        return thunkAPI.rejectWithValue(extract(error, 'Failed to load available plans'));
                    }
                });
            export const assignMemberToPlan = createAsyncThunk('partnerWorkspace/assignMemberToPlan', async ({
                userId,
                planId,
                startDate,
                partnerId
            }, thunkAPI) => {
                try {
                    await apiService.post('/barbellOSOutlet/plans/assign-member', {
                        user_id: userId,
                        plan_id: planId,
                        start_date: startDate || null
                    });
                    await thunkAPI.dispatch(refreshMembers(partnerId));
                    return true;
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to assign member to plan'));
                }
            });
            export const fetchMemberPaymentQuote = createAsyncThunk('partnerWorkspace/fetchMemberPaymentQuote', async ({
                memberId,
                planId
            }, thunkAPI) => {
                try {
                    const response = await apiService.get(`/barbellOSBilling/member/${memberId}/quote`, {
                        plan_id: planId
                    });
                    return response?.data?.data || null;
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to fetch payment quote'));
                }
            });
            export const initiateMemberUpiPayment = createAsyncThunk('partnerWorkspace/initiateMemberUpiPayment',
            async ({
                    memberId,
                    planId
                }, thunkAPI) => {
                    try {
                        const response = await apiService.post(
                            `/barbellOSBilling/member/${memberId}/upi/initiate`, {
                                plan_id: planId || null,
                                create_pending: false
                            });
                        return response?.data?.data || null;
                    } catch (error) {
                        return thunkAPI.rejectWithValue(extract(error, 'Failed to initiate UPI payment'));
                    }
                });
            export const confirmMemberUpiPayment = createAsyncThunk('partnerWorkspace/confirmMemberUpiPayment', async ({
                memberId,
                transactionId,
                amount,
                method,
                planId,
                startDate,
                partnerId
            }, thunkAPI) => {
                try {
                    await apiService.post(`/barbellOSBilling/member/${memberId}/upi/confirm`, {
                        transaction_id: transactionId || null,
                        amount,
                        method: method || 'UPI',
                        plan_id: planId || null,
                        start_date: startDate || null
                    });
                    await thunkAPI.dispatch(refreshMembers(partnerId));
                    await thunkAPI.dispatch(fetchBillingDashboard(partnerId));
                    return true;
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to confirm payment'));
                }
            });
            export const cancelMemberOnboarding = createAsyncThunk('partnerWorkspace/cancelMemberOnboarding', async ({
                memberId,
                partnerId
            }, thunkAPI) => {
                try {
                    await apiService.post(`/barbellOSBilling/member/${memberId}/payment/cancel`);
                    if (partnerId) {
                        await thunkAPI.dispatch(refreshMembers(partnerId));
                        await thunkAPI.dispatch(fetchBillingDashboard(partnerId));
                    }
                    return true;
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to cancel onboarding'));
                }
            });
            export const fetchBillingDashboard = createAsyncThunk('partnerWorkspace/fetchBillingDashboard', async (
                partnerId, thunkAPI) => {
                try {
                    const response = await apiService.get(
                        `/barbellOSBilling/partner/${partnerId}/dashboard`);
                    return response?.data?.data || {
                        partner_upi_id: null,
                        summary: {
                            pending_count: 0,
                            pending_amount: 0,
                            collections_count: 0,
                            collections_amount: 0,
                            next_payments_count: 0,
                            overdue_count: 0,
                            on_hold_count: 0
                        },
                        pending_payments: [],
                        payment_history: [],
                        next_payments: [],
                        overdue_payments: [],
                        on_hold_payments: []
                    };
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to load billing dashboard'));
                }
            });
            export const fetchPartnerSubscriptions = createAsyncThunk('partnerWorkspace/fetchPartnerSubscriptions',
                async (partnerId, thunkAPI) => {
                    try {
                        const response = await apiService.get(
                            `/barbellOSBilling/partner/${partnerId}/subscriptions`);
                        return Array.isArray(response?.data?.data) ? response.data.data : [];
                    } catch (error) {
                        return thunkAPI.rejectWithValue(extract(error, 'Failed to load outlet subscriptions'));
                    }
                });
            export const fetchAlertQueue = createAsyncThunk('partnerWorkspace/fetchAlertQueue', async (_, thunkAPI) => {
                try {
                    const response = await apiService.get('/barbellOSBilling/alerts');
                    return Array.isArray(response?.data?.data) ? response.data.data : [];
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to load alert queue'));
                }
            });
            export const triggerManualAlert = createAsyncThunk('partnerWorkspace/triggerManualAlert', async ({
                memberId,
                outletId,
                partnerId,
                mode = 'email',
                alertType = null,
                scheduledFor = null,
                message = ''
            }, thunkAPI) => {
                try {
                    await apiService.post('/barbellOSBilling/partner/manual-alert', {
                        member_id: memberId,
                        outlet_id: outletId,
                        mode,
                        alert_type: alertType,
                        scheduled_for: scheduledFor,
                        message
                    });
                    await thunkAPI.dispatch(fetchBillingDashboard(partnerId));
                    await thunkAPI.dispatch(fetchAlertQueue());
                    return true;
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to trigger manual alert'));
                }
            });
            export const triggerOverdueAction = createAsyncThunk('partnerWorkspace/triggerOverdueAction', async ({
                memberId,
                outletId,
                partnerId,
                sendDiscontinueMessage = false,
                disableMember = false,
                sendSms = false,
                sendEmail = true
            }, thunkAPI) => {
                try {
                    await apiService.post('/barbellOSBilling/partner/overdue-action', {
                        member_id: memberId,
                        outlet_id: outletId,
                        send_discontinue_message: sendDiscontinueMessage,
                        disable_member: disableMember,
                        send_sms: false,
                        send_email: sendEmail
                    });
                    await thunkAPI.dispatch(fetchBillingDashboard(partnerId));
                    await thunkAPI.dispatch(refreshMembers(partnerId));
                    return true;
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to execute overdue action'));
                }
            });
            export const requestPlanUpgrade = createAsyncThunk('partnerWorkspace/requestPlanUpgrade', async ({
                outletId,
                targetTier,
                partnerId
            }, thunkAPI) => {
                try {
                    await apiService.post('/barbellOSBilling/partner/plan-upgrade/request', {
                        outlet_id: outletId,
                        target_tier: targetTier
                    });
                    if (partnerId) {
                        await thunkAPI.dispatch(fetchPartnerSubscriptions(partnerId));
                    }
                    return true;
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to request plan upgrade'));
                }
            });
            export const updateOutletSignupFee = createAsyncThunk('partnerWorkspace/updateOutletSignupFee', async ({
                outletId,
                signupFeeDefault
            }, thunkAPI) => {
                try {
                    const response = await apiService.put('/barbellOSBilling/partner/outlet/signup-fee', {
                        outlet_id: outletId,
                        signup_fee_default: signupFeeDefault
                    });
                    return response?.data?.data || null;
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to update signup fee'));
                }
            });
            export const startMembershipHold = createAsyncThunk('partnerWorkspace/startMembershipHold', async ({
                memberId,
                outletId,
                partnerId,
                holdMonths,
                holdingChargePerMonth
            }, thunkAPI) => {
                try {
                    await apiService.post('/barbellOSBilling/partner/membership-hold/start', {
                        member_id: memberId,
                        outlet_id: outletId,
                        hold_months: holdMonths,
                        holding_charge_per_month: holdingChargePerMonth
                    });
                    await thunkAPI.dispatch(fetchBillingDashboard(partnerId));
                    await thunkAPI.dispatch(refreshMembers(partnerId));
                    return true;
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to start membership hold'));
                }
            });
            export const resumeMembershipHold = createAsyncThunk('partnerWorkspace/resumeMembershipHold', async ({
                memberId,
                outletId,
                partnerId,
                amount,
                transactionId,
                method = 'UPI'
            }, thunkAPI) => {
                try {
                    await apiService.post('/barbellOSBilling/partner/membership-hold/resume', {
                        member_id: memberId,
                        outlet_id: outletId,
                        amount,
                        transaction_id: transactionId || null,
                        method
                    });
                    await thunkAPI.dispatch(fetchBillingDashboard(partnerId));
                    await thunkAPI.dispatch(refreshMembers(partnerId));
                    return true;
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to resume membership hold'));
                }
            });
            export const editMember = createAsyncThunk('partnerWorkspace/editMember', async ({
                memberId,
                payload,
                partnerId
            }, thunkAPI) => {
                try {
                    await apiService.put('/barbellOSMember/members/edit', payload, {
                        params: {
                            member_id: memberId
                        }
                    });
                    await thunkAPI.dispatch(refreshMembers(partnerId));
                    return true;
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to edit member'));
                }
            });
            export const toggleMember = createAsyncThunk('partnerWorkspace/toggleMember', async ({
                memberId,
                activate,
                partnerId
            }, thunkAPI) => {
                try {
                    await apiService.put(
                        `/barbellOSMember/members/${memberId}/${activate ? 'activate' : 'deactivate'}`);
                    await thunkAPI.dispatch(refreshMembers(partnerId));
                    return true;
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to update member status'));
                }
            });
            export const deleteMember = createAsyncThunk('partnerWorkspace/deleteMember', async ({
                memberId,
                partnerId
            }, thunkAPI) => {
                try {
                    await apiService.delete(`/barbellOSMember/members/${memberId}/delete`);
                    await thunkAPI.dispatch(refreshMembers(partnerId));
                    return true;
                } catch (error) {
                    return thunkAPI.rejectWithValue(extract(error, 'Failed to delete member'));
                }
            });
            const partnerWorkspaceSlice = createSlice({
                name: 'partnerWorkspace',
                initialState,
                reducers: {
                    setSelectedOutletId: (state, action) => {
                        state.selectedOutletId = action.payload;
                    },
                    setSelectedOutletFilter: (state, action) => {
                        state.selectedMemberOutletFilter = action.payload;
                    }
                },
                extraReducers: (builder) => {
                    builder.addCase(bootstrapPartnerWorkspace.pending, (state) => {
                        state.status = 'loading';
                        state.error = null;
                    }).addCase(bootstrapPartnerWorkspace.fulfilled, (state, action) => {
                        state.status = 'succeeded';
                        state.partnerId = action.payload.partnerId;
                        state.partnerName = action.payload.partnerName;
                        state.outlets = action.payload.outlets;
                        state.members = action.payload.members;
                        state.plans = action.payload.plans;
                        state.selectedOutletId = action.payload.selectedOutletId;
                        state.selectedMemberOutletFilter = action.payload.selectedOutletId || 'ALL';
                        state.emailVerificationRequired = action.payload
                            .emailVerificationRequired !== false;
                        state.planCharges = action.payload.planCharges || state.planCharges;
                    }).addCase(bootstrapPartnerWorkspace.rejected, (state, action) => {
                        state.status = 'failed';
                        state.error = action.payload || 'Unable to load partner data';
                    }).addCase(fetchPlansByOutlet.fulfilled, (state, action) => {
                        state.plans = action.payload.plans;
                        state.selectedOutletId = action.payload.outletId;
                    }).addCase(fetchPlansForOutlets.fulfilled, (state, action) => {
                        state.outletPlansByOutlet = action.payload || {};
                    }).addCase(fetchPartnerSubscriptions.fulfilled, (state, action) => {
                        const map = {};
                        (action.payload || []).forEach((row) => {
                            if (row?.outlet_id) {
                                map[String(row.outlet_id)] = row;
                            }
                        });
                        state.outletSubscriptions = map;
                    }).addCase(refreshMembers.fulfilled, (state, action) => {
                        state.members = action.payload;
                    }).addCase(fetchBillingDashboard.fulfilled, (state, action) => {
                        state.billingSummary = action.payload;
                    }).addCase(fetchAlertQueue.fulfilled, (state, action) => {
                        state.alertQueue = action.payload || [];
                    }).addCase(updateOutletSignupFee.fulfilled, (state, action) => {
                        const outletId = action.payload?.outlet_id;
                        if (!outletId) {
                            return;
                        }
                        state.outlets = (state.outlets || []).map((outlet) => String(outlet.id) ===
                            String(outletId) ? {
                                ...outlet,
                                signup_fee_default: action.payload.signup_fee_default
                            } : outlet);
                    }).addMatcher((action) => action.type.startsWith('partnerWorkspace/') && action.type
                        .endsWith('/pending') && !action.type.includes('bootstrap') && !action.type
                        .includes('fetchPlansByOutlet') && !action.type.includes('refreshMembers') && !
                        action.type.includes('fetchBillingDashboard'), (state) => {
                            state.actionStatus = 'loading';
                            state.error = null;
                        }).addMatcher((action) => action.type.startsWith('partnerWorkspace/') && action
                        .type.endsWith('/fulfilled') && !action.type.includes('bootstrap') && !action
                        .type.includes('fetchPlansByOutlet') && !action.type.includes(
                        'refreshMembers') && !action.type.includes('fetchBillingDashboard'), (
                        state) => {
                            state.actionStatus = 'succeeded';
                        }).addMatcher((action) => action.type.startsWith('partnerWorkspace/') && action
                        .type.endsWith('/rejected'), (state, action) => {
                            state.actionStatus = 'failed';
                            state.error = action.payload || 'Operation failed';
                        });
                }
            });
            export const {
                setSelectedOutletId,
                setSelectedOutletFilter
            } = partnerWorkspaceSlice.actions;
            export default partnerWorkspaceSlice.reducer;
